import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 🔥 Wiki - Seção PAGs (Termômetro de Atratividade)
 * 
 * Documentação completa sobre o sistema de PAGs (Termômetro de Atratividade dos Produtos).
 * Explica como funciona o cálculo, o que significa cada componente, e como é usado na loja.
 * Voltada para produtores (não técnica).
 */
const PagsWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:fire" />
          <span>Termômetro de Atratividade (PAGs)</span>
        </div>
        <p className={styles.heroText}>
          Entenda como funciona o termômetro de atratividade dos seus produtos.
          Um indicador visual que ajuda afiliados a identificar produtos mais "quentes" para promover.
        </p>
      </section>

      {/* O que são PAGs */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que são PAGs?</span>
          <p className={styles.sectionSubtitle}>
            Termômetro de Atratividade dos Produtos
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              <strong>PAGs</strong> (Pontos de Atratividade Geral) é um <strong>termômetro visual</strong> que 
              aparece na loja para ajudar afiliados a identificar produtos com maior potencial de ganho.
            </p>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              O termômetro varia de <strong>0 a 200 pontos</strong> e é calculado automaticamente 
              com base em três fatores: <strong>vendas recentes</strong>, <strong>comissão média</strong> 
              e <strong>quantidade de afiliados ativos</strong> promovendo o produto.
            </p>
          </div>
        </div>

        <Row className="g-4 mt-3">
          <Col lg={6}>
            <div className={styles.participantCard} style={{ borderLeft: '4px solid #10b981' }}>
              <div className={`${styles.cardIcon} ${styles.iconProductHot}`}>
                <Icon icon="mdi:chart-line" />
              </div>
              <div className={styles.cardContent}>
                <span className={styles.cardTitle}>Produtos "Quentes"</span>
                <p>
                  Produtos com <strong>PAGs alto (150-200)</strong> são considerados "quentes" e 
                  têm maior probabilidade de gerar vendas para afiliados.
                </p>
                <div className={styles.exampleBox} style={{ background: '#ecfdf5' }}>
                  <Icon icon="mdi:fire" style={{ color: '#10b981' }} />
                  <span style={{color: '#4B5563'}}><strong style={{color: '#10b981'}}>Indicador:</strong> Barra de progresso quase completa</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard} style={{ borderLeft: '4px solid #f59e0b' }}>
              <div className={`${styles.cardIcon} ${styles.iconProductWarm}`}>
                <Icon icon="mdi:chart-bar" />
              </div>
              <div className={styles.cardContent}>
                <span className={styles.cardTitle}>Produtos "Mornos"</span>
                <p>
                  Produtos com <strong>PAGs médio (50-150)</strong> têm potencial moderado 
                  e podem ser promovidos com estratégias específicas.
                </p>
                <div className={styles.exampleBox} style={{ background: '#fffbeb' }}>
                  <Icon icon="mdi:thermometer" style={{ color: '#f59e0b' }} />
                  <span style={{color: '#4B5563'}}><strong style={{color: '#f59e0b'}}>Indicador:</strong> Barra de progresso pela metade</span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como é Calculado */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como é Calculado o PAGs?</span>
          <p className={styles.sectionSubtitle}>
            Fórmula baseada em 3 fatores principais
          </p>
        </div>

        <div className={styles.infoCard} style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(37, 99, 235, 0.05))' }}>
          <Icon icon="mdi:calculator" className={styles.infoIcon} style={{ color: '#3b82f6' }} />
          <div>
            <p style={{ marginBottom: '8px' }}>
              <strong>Fórmula do PAGs:</strong>
            </p>
            <p style={{ 
              fontSize: '1.1rem', 
              fontFamily: 'monospace', 
              background: 'white', 
              padding: '12px', 
              borderRadius: '8px',
              marginBottom: '8px',
              color: '#4B5563'
            }}>
              (Vendas por Dia × 6) + (Comissão Média × 1.5) + (Afiliados Ativos × 2)
            </p>
            <p style={{ marginBottom: 0, fontSize: '0.9rem', color: '#6B7280' }}>
              O resultado é limitado a um máximo de <strong>200 pontos</strong>.
            </p>
          </div>
        </div>

        <Row className="g-4 mt-4">
          <Col lg={4}>
            <div className={styles.participantCard}>
              <div className={styles.cardIcon} style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                <Icon icon="mdi:sale" color="#3b82f6"/>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardBadge} style={{ background: '#3b82f6', color: 'white' }}>
                  6 pontos/venda/dia
                </div>
                <span className={styles.cardTitle}>Vendas por Dia</span>
                <p>
                  Calculado com base nas <strong>vendas pagas dos últimos 30 dias</strong>.
                  Quanto mais vendas recentes, maior a pontuação.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:calculator" />
                  <span><strong>Exemplo:</strong> 120 vendas em 30 dias = 4 vendas/dia = <strong>24 pontos</strong></span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <div className={styles.participantCard}>
              <div className={styles.cardIcon} style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                <Icon icon="mdi:cash-multiple" color="#10b981"/>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardBadge} style={{ background: '#10b981', color: 'white' }}>
                  1.5 pontos/real
                </div>
                <span className={styles.cardTitle}>Comissão Média</span>
                <p>
                  Média das <strong>comissões pagas aos afiliados</strong> nas vendas dos últimos 30 dias.
                  Produtos com comissões maiores geram mais pontos.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:calculator" />
                  <span><strong>Exemplo:</strong> R$ 240 de comissão média = <strong>360 pontos</strong></span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <div className={styles.participantCard}>
              <div className={styles.cardIcon} style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
                <Icon icon="mdi:account-group" color="#8b5cf6"/>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardBadge} style={{ background: '#8b5cf6', color: 'white' }}>
                  2 pontos/afiliado
                </div>
                <span className={styles.cardTitle}>Afiliados Ativos</span>
                <p>
                  Número de <strong>afiliados distintos</strong> que geraram pelo menos uma venda 
                  paga nos últimos 30 dias.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:calculator" />
                  <span><strong>Exemplo:</strong> 3 afiliados ativos = <strong>6 pontos</strong></span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Exemplo Prático */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Exemplo Prático de Cálculo</span>
          <p className={styles.sectionSubtitle}>
            Como o PAGs é calculado na prática
          </p>
        </div>

        <div className={styles.configCard}>
          <div className={styles.configIcon} style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
            <Icon icon="mdi:calculator-variant" />
          </div>
          <div className={styles.configContent}>
            <span className={styles.configTitle}>
              Produto: "Curso de Marketing Digital"
            </span>

            <Row className="g-3 mb-4">
              <Col md={4}>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>Vendas (30 dias)</div>
                  <div className={styles.statValue} style={{ color: '#3b82f6' }}>120</div>
                  <div className={styles.statSubtext}>4 vendas/dia</div>
                </div>
              </Col>
              <Col md={4}>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>Comissão Média</div>
                  <div className={styles.statValue} style={{ color: '#10b981' }}>R$ 240</div>
                  <div className={styles.statSubtext}>por venda</div>
                </div>
              </Col>
              <Col md={4}>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>Afiliados Ativos</div>
                  <div className={styles.statValue} style={{ color: '#8b5cf6' }}>3</div>
                  <div className={styles.statSubtext}>promovendo</div>
                </div>
              </Col>
            </Row>

            <div className={styles.calculationResult}>
              <div className={styles.calculationLabel}>Cálculo do PAGs:</div>
              <div className={styles.calculationSteps}>
                <div>(4 vendas/dia × 6) = <strong style={{ color: '#3b82f6' }}>24 pontos</strong></div>
                <div style={{ marginTop: '8px' }}>+ (R$ 240 × 1.5) = <strong style={{ color: '#10b981' }}>360 pontos</strong></div>
                <div style={{ marginTop: '8px' }}>+ (3 afiliados × 2) = <strong style={{ color: '#8b5cf6' }}>6 pontos</strong></div>
                <div className={styles.calculationTotal}>
                  Total: 390 pontos → <span style={{ color: '#10b981' }}>200 PAGs</span> (máximo)
                </div>
              </div>
              <div className={styles.successBox}>
                <Icon icon="mdi:check-circle" style={{ color: '#10b981', fontSize: '20px' }} />
                <span>
                  Este produto aparece <strong>primeiro</strong> na loja, pois tem o maior PAGs!
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Como é Usado na Loja */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como é Usado na Loja?</span>
          <p className={styles.sectionSubtitle}>
            Ordenação automática e visualização do termômetro
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={styles.cardIcon} style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                <Icon icon="mdi:sort" color='#3b82f6'/>
              </div>
              <div className={styles.cardContent}>
                <span className={styles.cardTitle}>Ordenação Automática</span>
                <p>
                  Os produtos na loja são <strong>automaticamente ordenados</strong> por PAGs, 
                  do maior para o menor. Produtos com maior atratividade aparecem primeiro!
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:arrow-up" color='#3b82f6' />
                  <span><strong>Topo da loja:</strong> Produtos com PAGs mais alto</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={styles.cardIcon} style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                <Icon icon="mdi:chart-line-variant" color="#10b981"/>
              </div>
              <div className={styles.cardContent}>
                <span className={styles.cardTitle}>Barra de Progresso</span>
                <p>
                  Cada produto na loja exibe uma <strong>barra de progresso</strong> com o 
                  valor do PAGs (0 a 200). Quanto maior a barra, mais "quente" o produto.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:progress-check" />
                  <span><strong>Visual:</strong> Barra azul com círculo mostrando o score</span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Dicas para Aumentar PAGs */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Aumentar o PAGs do Meu Produto?</span>
          <p className={styles.sectionSubtitle}>
            Dicas práticas para tornar seus produtos mais atrativos
          </p>
        </div>

        <Row className="g-4">
          <Col lg={12}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon} style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                <Icon icon="mdi:trending-up" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Aumente as Vendas
                </span>
                <p>
                  Quanto mais <strong>vendas recentes</strong> seu produto tiver, maior será o PAGs. 
                  Invista em marketing, ofertas e estratégias de divulgação para aumentar o volume de vendas.
                </p>
                <div className={styles.tipBox}>
                  <Icon icon="mdi:lightbulb" className={styles.iconPlatform} style={{ fontSize: '20px', flexShrink: 0 }} />
                  <span>
                    <strong>Dica:</strong> Produtos com 4+ vendas por dia geram pontos significativos!
                  </span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon} style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                <Icon icon="mdi:cash-multiple" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Ofereça Boas Comissões
                </span>
                <p>
                  Produtos com <strong>comissões maiores</strong> geram mais pontos no cálculo. 
                  Considere oferecer comissões atrativas (20-30%) para incentivar afiliados a promover seu produto.
                </p>
                <div className={styles.tipBox}>
                  <Icon icon="mdi:lightbulb" className={styles.iconAffiliate} style={{ fontSize: '20px', flexShrink: 0 }} />
                  <span>
                    <strong>Dica:</strong> Comissão de R$ 200+ por venda gera 300+ pontos sozinha!
                  </span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>3</div>
              <div className={styles.configIcon} style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
                <Icon icon="mdi:account-group" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Tenha Múltiplos Afiliados Ativos
                </span>
                <p>
                  Produtos promovidos por <strong>múltiplos afiliados</strong> geram mais pontos. 
                  Incentive diferentes afiliados a promoverem seu produto através de estratégias de marketing.
                </p>
                <div className={styles.tipBox}>
                  <Icon icon="mdi:lightbulb" style={{ color: '#8b5cf6', fontSize: '20px', flexShrink: 0 }} />
                  <span>
                    <strong>Dica:</strong> 5+ afiliados ativos geram 10+ pontos adicionais!
                  </span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* FAQ */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Perguntas Frequentes</span>
          <p className={styles.sectionSubtitle}>
            Dúvidas comuns sobre o sistema de PAGs
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.infoCard} style={{ height: '100%' }}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ fontWeight: '600', marginBottom: '8px' }}>
                  Com que frequência o PAGs é atualizado?
                </p>
                <p style={{ marginBottom: 0, fontSize: '0.95rem' }}>
                  O PAGs é <strong>calculado em tempo real</strong>, mas os resultados são 
                  <strong> cacheados por 24 horas</strong> para melhor performance. Após uma nova venda, 
                  o cache é invalidado e o cálculo é atualizado automaticamente.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.infoCard} style={{ height: '100%' }}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ fontWeight: '600', marginBottom: '8px' }}>
                  Por que meu produto tem PAGs baixo?
                </p>
                <p style={{ marginBottom: 0, fontSize: '0.95rem' }}>
                  PAGs baixo geralmente indica: <strong>poucas vendas recentes</strong>, 
                  <strong> comissões baixas</strong> ou <strong>poucos afiliados promovendo</strong>. 
                  Foque em aumentar vendas e atrair mais afiliados para o produto.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.infoCard} style={{ height: '100%' }}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ fontWeight: '600', marginBottom: '8px' }}>
                  O PAGs considera apenas os últimos 30 dias?
                </p>
                <p style={{ marginBottom: 0, fontSize: '0.95rem' }}>
                  Sim! O cálculo considera apenas <strong>vendas pagas dos últimos 30 dias</strong>. 
                  Isso garante que o termômetro reflita o <strong>momentum atual</strong> do produto, 
                  não apenas vendas antigas.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.infoCard} style={{ height: '100%' }}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ fontWeight: '600', marginBottom: '8px' }}>
                  Posso ver o PAGs dos meus produtos?
                </p>
                <p style={{ marginBottom: 0, fontSize: '0.95rem' }}>
                  Sim! O PAGs é exibido na <strong>loja pública</strong> para todos os produtos. 
                  Acesse a loja e veja o termômetro de cada produto. Produtos com maior PAGs 
                  aparecem primeiro na lista.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default PagsWiki;

